import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { observer } from 'mobx-react'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  Block,
  Checkbox,
  ExternalLink,
  Heading,
  Paragraph,
  RadioButton,
  RadioButtonGroup,
  StatusText,
} from 'suomifi-ui-components'
import { AetOrdererUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { PlaceOfMarriage } from 'todistukset-common/src/vtj/types/place-of-marriage'
import {
  isPlaceOfMarriageUnsupported,
  validatePlaceOfMarriage,
  validateAetTodistusLanguage,
} from 'todistukset-common/src/vtj/validation'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  FormContainer,
  InfoInlineAlert,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useAetOrdererFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-orderer-form-actions'
import { useAetOrdererForm } from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { expectedAetTodistusLanguage } from 'todistukset-common/src/vtj/language.util'

const AetAdditionalSelectionsStep: StepComponent = observer(
  forwardRef(function AetAdditionalSelectionsStep(
    { orderFlowName, titleId },
    ref
  ) {
    const { t } = useTranslation()
    const {
      steps: {
        [orderFlowName]: { showStepValidationErrors: showErrors },
      },
    } = useStore()
    const form = useAetOrdererForm()
    const {
      setPlaceOfMarriage,
      setTodistusLanguage,
      setIsKirkollinenVihkiminen,
    } = useAetOrdererFormActions()

    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'placeOfMarriage',
      'todistusLanguage',
    ])

    const placeOfMarriageValue = finalEditableFieldValue(form.placeOfMarriage)
    const todistusLanguageValue = finalEditableFieldValue(form.todistusLanguage)
    const placeAndLanguageConflict =
      todistusLanguageValue &&
      placeOfMarriageValue &&
      placeOfMarriageValue in expectedAetTodistusLanguage &&
      !expectedAetTodistusLanguage[
        placeOfMarriageValue as keyof typeof expectedAetTodistusLanguage
      ].includes(todistusLanguageValue)

    const displayWrongCertificationInlineError = isPlaceOfMarriageUnsupported(
      finalEditableFieldValue(form.placeOfMarriage)
    )

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetOrdererAdditionalSelectionsMainTitle" />
          </Heading>
        </Block>
        <Block variant="section" mt="m">
          <Heading variant="h3">
            <LocalizedString stringKey="aetOrdererAdditionalSelectionsPlaceOfWeddingTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOrdererAdditionalSelectionsPlaceOfWeddingDescription_paragraphs" />
          <RadioButtonGroup
            mt="l"
            id="placeOfWedding"
            data-test-id="placeOfWedding"
            name="placeOfWedding"
            labelText={t('aetOrdererAdditionalSelectionsPlaceOfWeddingLabel')}
            value={placeOfMarriageValue ?? undefined}
            onChange={setPlaceOfMarriage}
          >
            <RadioButton
              value={PlaceOfMarriage.FINLAND}
              forwardedRef={refs.placeOfMarriage}
            >
              <LocalizedString stringKey="aetOrdererAdditionalSelectionsPlaceOfWeddingLabel_finland" />
            </RadioButton>
            <RadioButton value={PlaceOfMarriage.NORDICS}>
              <LocalizedString stringKey="aetOrdererAdditionalSelectionsPlaceOfWeddingLabel_nordics" />
            </RadioButton>
            <RadioButton value={PlaceOfMarriage.OTHER}>
              <LocalizedString stringKey="aetOrdererAdditionalSelectionsPlaceOfWeddingLabel_order" />
            </RadioButton>
          </RadioButtonGroup>{' '}
          {!displayWrongCertificationInlineError && (
            <RadioButtonError
              {...errorStatus({
                field: 'placeOfMarriage',
                form,
                showErrors,
                t,
              })}
            />
          )}
          {displayWrongCertificationInlineError && (
            <InfoInlineAlert
              status="error"
              labelText={t(
                'aetOrdererAdditionalSelectionsWrongCertificateTitle'
              )}
            >
              <LocalizedParagraphs stringKey="aetOrdererAdditionalSelectionsWrongCertificateDescription_paragraphs" />
              <Paragraph>
                <ExternalLink
                  href={t(
                    'aetOrdererAdditionalSelectionsWrongCertificateLinkHref'
                  )}
                  labelNewWindow={t('opensInNewWindow')}
                  underline="initial"
                >
                  <LocalizedString stringKey="aetOrdererAdditionalSelectionsWrongCertificateLinkText" />
                </ExternalLink>
              </Paragraph>
            </InfoInlineAlert>
          )}
        </Block>
        <Block variant="section" mt="m">
          <RadioButtonGroup
            mt="l"
            id="todistusLanguage"
            data-test-id="todistusLanguage"
            name="todistusLanguage"
            labelText={t('aetOrdererAdditionalSelectionsTodistusLanguageLabel')}
            value={todistusLanguageValue ?? undefined}
            onChange={setTodistusLanguage}
          >
            <RadioButton
              value={LanguageCode.FI}
              forwardedRef={refs.todistusLanguage}
            >
              <LocalizedString stringKey="aetOrdererAdditionalSelectionsTodistusLanguageLabel_finnish" />
            </RadioButton>
            <RadioButton value={LanguageCode.SV}>
              <LocalizedString stringKey="aetOrdererAdditionalSelectionsTodistusLanguageLabel_swedish" />
            </RadioButton>
          </RadioButtonGroup>{' '}
          <RadioButtonError
            {...errorStatus({
              field: 'todistusLanguage',
              form,
              showErrors,
              t,
            })}
          />
          {placeAndLanguageConflict && (
            <InfoInlineAlert aria-live="polite" mb="xxl">
              <LocalizedParagraphs stringKey="aetOrdererAdditionalSelectionsTodistusLanguageNotMatching_paragraphs" />
            </InfoInlineAlert>
          )}
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetOrdererAdditionalSelectionsKirkollinenVihkiminenTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOrdererAdditionalSelectionsKirkollinenVihkiminenDescription_paragraphs" />
          <Checkbox
            mt="xl"
            mb="xxl"
            variant="small"
            checked={
              finalEditableFieldValue(form.isKirkollinenVihkiminen) ?? false
            }
            onClick={setIsKirkollinenVihkiminen}
          >
            <LocalizedString stringKey="aetOrdererAdditionalSelectionsKirkollinenVihkiminenCheckboxLabel" />
          </Checkbox>
        </Block>
      </FormContainer>
    )
  })
)

export default AetAdditionalSelectionsStep

const RadioButtonError: React.FC<ReturnType<typeof errorStatus>> = ({
  status,
  statusText = '',
}) =>
  status === 'error' ? (
    <StatusText ariaLiveMode="polite" status="error">
      {statusText}
    </StatusText>
  ) : null

function errorStatus(params: {
  field: keyof ReturnType<typeof getErrors>
  form: AetOrdererUiForm
  showErrors: boolean
  t: TFunction
}) {
  return getErrorStatus({
    ...params,
    errors: {
      ...getErrors(params.form),
    },
  })
}

function getErrors(form: AetOrdererUiForm) {
  return {
    placeOfMarriage: validatePlaceOfMarriage(
      finalEditableFieldValue(form.placeOfMarriage)
    ),
    todistusLanguage: validateAetTodistusLanguage(
      finalEditableFieldValue(form.todistusLanguage)
    ),
  }
}
